/**
 * CancelReasonStep - Inline step for collecting cancellation reason in the paywall
 *
 * This is an inline variant of the modal-cancel-membership component,
 * designed to render within the PaymentPaywallModal step flow.
 * Reuses the same cancellation reasons and validation logic.
 */

import { useState } from 'react';
import { CancellationReason } from '@/api/generated-catch-backend/model';
import '../paywall-reset.css';

// Same reasons as modal-cancel-membership
const CANCELLATION_REASONS = [
  { value: CancellationReason.TOO_EXPENSIVE, label: 'Too expensive' },
  { value: CancellationReason.NOT_USEFUL, label: "I don't find it useful" },
  { value: CancellationReason.JUST_TESTING, label: 'I only wanted to try it' },
  { value: CancellationReason.TECHNICAL_ISSUES, label: 'Technical issues' },
  {
    value: CancellationReason.CONCERNED_ABOUT_RESULTS,
    label: 'Concerned about my results',
  },
  { value: CancellationReason.OTHER, label: 'Other' },
] as const;

interface CancelReasonStepProps {
  onSubmit: (reason: CancellationReason, reasonDescription?: string) => void;
  onBack: () => void;
  isSubmitting: boolean;
  error: string | null;
}

export function CancelReasonStep({
  onSubmit,
  onBack,
  isSubmitting,
  error,
}: CancelReasonStepProps) {
  const [selectedReason, setSelectedReason] =
    useState<CancellationReason | null>(null);
  const [reasonDescription, setReasonDescription] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = () => {
    if (!selectedReason) {
      setValidationError('Please select a reason');
      return;
    }
    if (selectedReason === CancellationReason.OTHER && !reasonDescription.trim()) {
      setValidationError('Please provide additional details');
      return;
    }
    setValidationError(null);
    onSubmit(selectedReason, reasonDescription.trim() || undefined);
  };

  return (
    <div className="paywall-reset flex flex-col">
      {/* Back arrow */}
      <button
        onClick={onBack}
        disabled={isSubmitting}
        className="mb-4 flex h-8 w-8 items-center justify-center self-start text-[#183344] transition-opacity hover:opacity-70 disabled:opacity-50"
        aria-label="Go back"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M19 12H5M5 12L12 19M5 12L12 5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Header */}
      <h2 className="mb-6 text-2xl font-bold leading-tight text-[#183344]">
        We're sorry to see you go. Before you cancel, can you tell us why?
      </h2>

      {/* Error display */}
      {(error || validationError) && (
        <div className="mb-4 rounded-lg bg-red-50 p-3 text-center text-sm text-red-600">
          {error || validationError}
        </div>
      )}

      {/* Reason options - card style without visible radio */}
      <div className="mb-4 flex flex-col gap-2">
        {CANCELLATION_REASONS.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            onClick={() => {
              setSelectedReason(value);
              setValidationError(null);
            }}
            className={`w-full rounded-xl border px-4 py-4 text-left text-sm text-[#183344] shadow-sm transition-all ${
              selectedReason === value
                ? 'border-[#183344] bg-[#F8FAFC]'
                : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Additional details textarea - only appears when "Other" is selected */}
      {selectedReason === CancellationReason.OTHER && (
        <div className="mb-6 flex flex-col gap-2">
          <label className="text-sm text-[#64748B]">
            Provide additional details
          </label>
          <textarea
            value={reasonDescription}
            onChange={(e) => {
              setReasonDescription(e.target.value);
              if (validationError) setValidationError(null);
            }}
            placeholder=""
            rows={4}
            className="w-full rounded-xl border border-[#E2E8F0] bg-white p-4 text-sm text-[#183344] shadow-sm placeholder-[#94A3B8] focus:border-[#183344] focus:outline-none"
          />
        </div>
      )}

      {/* Cancel Subscription button */}
      <button
        onClick={handleSubmit}
        disabled={isSubmitting || !selectedReason}
        className="flex w-full items-center justify-center rounded-full bg-[#183344] px-5 py-4 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
      >
        {isSubmitting ? 'Processing...' : 'Cancel Subscription'}
      </button>
    </div>
  );
}
